/**
 * Load and apply admin-published PLAY hub category icon masks.
 * Custom masks cover the full category card (not just the center art box).
 */
import { fetchHubIcons } from "../lib/cloud";
import type { HubIconCategoryId } from "../lib/cloud";
import { HUB_ICON_CATEGORIES } from "../../lib/hubIconTypes";

type IconEntry = { maskData: string; updatedAt: string };

let publishedIcons: Record<string, IconEntry> = {};
/** Session-only overrides (Icon Creator preview / unpublished edits). */
const sessionOverrides = new Map<string, string | null>();

let loadPromise: Promise<void> | null = null;

function cardEl(categoryId: string, root: ParentNode = document): HTMLElement | null {
  if (root === document) {
    return document.querySelector<HTMLElement>(
      `#gameHubGrid [data-hub-category="${CSS.escape(categoryId)}"]`,
    );
  }
  return root.querySelector<HTMLElement>(`[data-hub-category="${CSS.escape(categoryId)}"]`);
}

function defaultSvgEl(card: HTMLElement): SVGSVGElement | null {
  return card.querySelector<SVGSVGElement>(".gameHubArtSvg--default, .gameHubCardArt .gameHubArtSvg");
}

function ensureCustomEl(card: HTMLElement, categoryId: string): HTMLElement {
  let el = card.querySelector<HTMLElement>(":scope > .gameHubCustomIcon");
  if (!el) {
    el = document.createElement("span");
    el.className = "gameHubCustomIcon";
    el.setAttribute("aria-hidden", "true");
    el.dataset.hubCustomIcon = categoryId;
    card.insertBefore(el, card.firstChild);
  }
  return el;
}

function resolveMask(categoryId: string): string | null {
  if (sessionOverrides.has(categoryId)) {
    return sessionOverrides.get(categoryId) ?? null;
  }
  return publishedIcons[categoryId]?.maskData ?? null;
}

export function setHubIconSessionOverride(categoryId: string, maskData: string | null): void {
  sessionOverrides.set(categoryId, maskData);
  applyHubIconToCategory(categoryId);
}

export function clearHubIconSessionOverrides(): void {
  sessionOverrides.clear();
  refreshAllHubIcons();
}

export function getHubIconSessionOverrides(): Map<string, string | null> {
  return new Map(sessionOverrides);
}

/** Apply (or clear) custom icon on one card. Optional `root` for Icon Creator preview clones. */
export function applyHubIconToCategory(categoryId: string, root: ParentNode = document): void {
  const card = cardEl(categoryId, root);
  if (!card) return;
  const svg = defaultSvgEl(card);
  const art = card.querySelector<HTMLElement>(".gameHubCardArt");
  const mask = resolveMask(categoryId);
  // Remove legacy custom icons that lived inside the art box.
  art?.querySelectorAll(":scope > .gameHubCustomIcon").forEach((n) => n.remove());

  if (!mask) {
    svg?.classList.remove("hidden");
    art?.classList.remove("gameHubCardArt--customHidden");
    card.querySelector(".gameHubCardBody")?.classList.remove("gameHubCardBody--customHidden");
    card.classList.remove("gameHubCard--customIcon");
    const custom = card.querySelector<HTMLElement>(":scope > .gameHubCustomIcon");
    custom?.classList.add("hidden");
    custom?.style.removeProperty("--hub-icon-mask");
    return;
  }
  const custom = ensureCustomEl(card, categoryId);
  custom.style.setProperty("--hub-icon-mask", `url("${mask}")`);
  custom.classList.remove("hidden");
  svg?.classList.add("hidden");
  art?.classList.add("gameHubCardArt--customHidden");
  card.querySelector(".gameHubCardBody")?.classList.remove("gameHubCardBody--customHidden");
  card.classList.add("gameHubCard--customIcon");
}

export function refreshAllHubIcons(root: ParentNode = document): void {
  for (const id of HUB_ICON_CATEGORIES) {
    applyHubIconToCategory(id, root);
  }
}

export async function loadPublishedHubIcons(): Promise<void> {
  if (!loadPromise) {
    loadPromise = (async () => {
      try {
        publishedIcons = await fetchHubIcons();
      } catch {
        publishedIcons = {};
      } finally {
        loadPromise = null;
      }
    })();
  }
  await loadPromise;
  refreshAllHubIcons();
}

export function getPublishedHubIcons(): Readonly<Record<string, IconEntry>> {
  return publishedIcons;
}

export function setPublishedHubIcon(categoryId: HubIconCategoryId, maskData: string, updatedAt: string): void {
  publishedIcons = { ...publishedIcons, [categoryId]: { maskData, updatedAt } };
  sessionOverrides.delete(categoryId);
  applyHubIconToCategory(categoryId);
}

export function removePublishedHubIcon(categoryId: HubIconCategoryId): void {
  const next = { ...publishedIcons };
  delete next[categoryId];
  publishedIcons = next;
  sessionOverrides.delete(categoryId);
  applyHubIconToCategory(categoryId);
}

/** Mark built-in SVG art containers so custom overlays can hide them. */
export function markDefaultHubIconSvgs(): void {
  document.querySelectorAll<SVGSVGElement>("#gameHubGrid .gameHubArtSvg").forEach((svg) => {
    if (!svg.classList.contains("gameHubArtSvg--default") && !svg.classList.contains("gameHubCustomIcon")) {
      svg.classList.add("gameHubArtSvg--default");
    }
  });
}
